import React from 'react';
import DOMPurify from 'dompurify';
import {isString} from 'lodash';
import {
  removeWfTokens,
  removeAllTokens,
} from '@packages/systems/core/utils/EmbedUtils/all';
import {hasScript} from '@packages/systems/core/utils/HtmlUtils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Atom} from '@packages/systems/wfdl';
import type {AtomRenderProps} from '@packages/systems/wfdl';
import {createIntegratedRootAliasInstance} from '@packages/utilities/integrated-types';
import {List} from 'immutable';
import {
  useHtmlEmbedOpenCanvasRewriter,
  useOpenCanvas,
  useRuntimeEnvironment,
} from '@packages/systems/render';
import {prepareCustomCodeForExecutionInCanvasApp} from '@packages/systems/designer/canvas';
import {getInPojo, getString, getBoolean} from '@packages/utilities/data-utils';

const createMarkup = (value: string) => {
  return {
    __html: value,
  };
};

// React `dangerouslySetInnerHTML` is using `innerHTML` to set the content of a DOM element. By the specification:
// `script` elements inserted using `innerHTML` do not execute when they are inserted (source: https://www.w3.org/TR/2008/WD-html5-20080610/dom.html#innerhtml0)
// To by pass this limitation, we are using `createRange().createContextualFragment` to make sure that all the content of the Embed will be executed.
// Solution included here is inspired by https://github.com/christo-pr/dangerously-set-html-content?tab=readme-ov-file
const EmbedWithScriptsAllowedToExecuteDynamically = ({
  markup,
  ...attr
}: {
  markup: string;
}) => {
  const elRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!elRef || !elRef.current) {
      return;
    }

    const documentFragment = document
      .createRange()
      .createContextualFragment(
        prepareCustomCodeForExecutionInCanvasApp(markup)
      );

    elRef.current.innerHTML = '';
    elRef.current.appendChild(documentFragment);
  }, [markup]);

  return <div {...attr} ref={elRef} />;
};

const maybeAddIframe = (isIframe: boolean) => (classes: List<string>) => {
  return isIframe ? classes.push('w-iframe') : classes;
};

const maybeAddScript =
  (
    isScript: boolean,
    runtimeEnvironment: null | Environment,
    isInsideRTE: boolean | undefined
  ) =>
  (classes: List<string>) => {
    return (runtimeEnvironment === WebflowEnvironments.DESIGNER &&
      isInsideRTE) ||
      isScript
      ? classes.push('w-script')
      : classes;
  };

const DOMPURIFY_CONFIG = {
  FORCE_BODY: true, // fixes bug when <style></style> were removed upon sanitization
  ADD_TAGS: ['iframe', 'embed', 'link', 'use'],
  ADD_ATTR: [
    'allow',
    'allowfullscreen',
    'frameborder',
    'scrolling',
    'allowtransparency',
    'marginheight',
    'marginwidth',
    'enable-background',
    'mask-type',
  ],
} as const;

const isEmbedVisible = (conditions: unknown): boolean => {
  if (!Array.isArray(conditions) || conditions.length === 0) return true;
  return conditions.every((v) => v === true);
};

// Prefer the evaluated `content` field (written by the FF embed editor)
// so script presence tracks the latest save without a write-time meta
// update. Falls back to the legacy `meta.script` flag for unmigrated
// embeds where `data.content` is empty or missing.
export const getEmbedHasScript = (
  content: string | undefined,
  legacyScriptFlag: boolean
): boolean => {
  if (isString(content) && content !== '') {
    return hasScript(content);
  }
  return legacyScriptFlag;
};

export const getImpureEmbedValue = (
  embedHtml: string | undefined,
  content: string | undefined,
  isScript: boolean,
  isCompilable: boolean,
  visibilityConditions: unknown,
  token: undefined | string,
  runtimeEnvironment: null | Environment,
  isInsideRTE: boolean | undefined,
  customCodeExecutionAllowed: boolean
) => {
  const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;

  if (isDesigner && (isScript || isInsideRTE) && !customCodeExecutionAllowed) {
    // HTML embeds that contain <script> tags are not rendered in Designer for security reasons
    return '';
  } else if (isDesigner && !isEmbedVisible(visibilityConditions)) {
    // HTML embeds that are hidden by conditional visibility should not render their content.
    // This is so that any side effects like custom CSS from <style> blocks are not
    // applied and the user sees a result similar to what they would see on a published site,
    // where the node won't be rendered at all.
    return '';
  } else {
    // Check the evaluated `content` field first (used by the FF embed editor).
    // When present, it takes priority over the legacy `token` prop so that
    // saves from the new editor are reflected on the canvas immediately.
    //
    // `content` is returned raw here; the DOMPurify sanitization gate at the
    // HtmlEmbed render call site covers the Designer environment. Published
    // renders are intentionally unsanitized, matching the legacy `token` path.
    if (isString(content) && content !== '') {
      return content;
    }

    // Fall back to the legacy `embed.meta.html` path for unmigrated embeds.
    if (isString(token)) {
      // Legacy dynamic html embed where dynamic data is already built
      // into the string via dyn.bind.token.
      return token;
    }

    // For static html embeds, we need to check
    // if there's a need to remove token variables.
    return getStaticEmbedValue(
      content,
      embedHtml,
      isCompilable,
      runtimeEnvironment
    );
  }
};

const getStaticEmbedValue = (
  content: string | undefined,
  embedHtml: string | undefined,
  isCompilable: boolean,
  runtimeEnvironment: null | Environment
) => {
  // If embed html is compilable, we need to compile so extra tokens
  // get cleaned up and for example external {\{ token }} becomes {{ token }}.
  return runtimeEnvironment !== WebflowEnvironments.EXPORT && isCompilable
    ? removeAllTokens(embedHtml || '')
    : removeWfTokens(embedHtml || '');
};

/**
 * Exported separately from `Atom.createRender` so tests can render it as a
 * regular React component without spinning up the full WFDL render harness.
 * `Atom.createRender` is a brand wrapper — it returns the component unchanged.
 */
export const HtmlEmbedRender = ({
  attributes,
  classes,
  data,
  generateAttributes,
  token,
  environmentContext,
}: AtomRenderProps) => {
  // Schema is defined in context/wfdl/Embed.md.
  const content = getString(getInPojo(data, ['content']));
  const embedHtml = getString(getInPojo(data, ['embed', 'meta', 'html']));
  const legacyScriptFlag =
    getBoolean(getInPojo(data, ['embed', 'meta', 'script'])) ?? false;
  const isScript = getEmbedHasScript(content, legacyScriptFlag);
  const isCompilable =
    getBoolean(getInPojo(data, ['embed', 'meta', 'compilable'])) ?? false;
  const isIframe =
    getBoolean(getInPojo(data, ['embed', 'meta', 'iframe'])) ?? false;
  const isInsideRTE = getBoolean(getInPojo(data, ['insideRTE']));
  const visibilityConditions = getInPojo(data, ['visibility', 'conditions']);

  const runtimeEnvironment = useRuntimeEnvironment();
  const customCodeExecutionAllowed = Boolean(
    environmentContext.get('customCodeExecutionAllowed')
  );

  const impureValue = getImpureEmbedValue(
    embedHtml,
    content,
    isScript,
    isCompilable,
    visibilityConditions,
    token,
    runtimeEnvironment,
    isInsideRTE,
    customCodeExecutionAllowed
  );
  const isDesigner = runtimeEnvironment === WebflowEnvironments.DESIGNER;

  const isOpenCanvas = useOpenCanvas();
  const rewriteHtmlEmbedForOpenCanvas = useHtmlEmbedOpenCanvasRewriter();

  // To prevent XSS attacks in the Designer environment
  // that will use vulnerabilities like <img src="x" onerror="alert('XSS')">
  // we need to sanitize value even when it contains dynamic tokens.
  const sanitizedValue = React.useMemo(() => {
    if (isDesigner && !customCodeExecutionAllowed) {
      // @ts-expect-error - TS2769 - No overload matches this call.
      const sanitized = DOMPurify.sanitize(impureValue, DOMPURIFY_CONFIG);
      return sanitized;
    } else {
      return impureValue;
    }
  }, [impureValue, isDesigner, customCodeExecutionAllowed]);

  // For the Open Canvas, apply the viewport-unit rewrite to ensure viewport-relative units in CSS
  // are resolved against the frame rather than the iframe viewport.
  const value = React.useMemo(
    () =>
      isOpenCanvas
        ? rewriteHtmlEmbedForOpenCanvas(sanitizedValue)
        : sanitizedValue,
    [isOpenCanvas, sanitizedValue, rewriteHtmlEmbedForOpenCanvas]
  );

  const htmlClasses = classes
    .push('w-embed')
    .update(maybeAddIframe(isIframe))
    .update(maybeAddScript(isScript, runtimeEnvironment, isInsideRTE));

  return customCodeExecutionAllowed ? (
    <EmbedWithScriptsAllowedToExecuteDynamically
      {...generateAttributes(attributes, htmlClasses)}
      markup={value}
    />
  ) : (
    <div
      {...generateAttributes(attributes, htmlClasses)}
      dangerouslySetInnerHTML={createMarkup(value)}
    />
  );
};

const HtmlEmbed = Atom.createRender(HtmlEmbedRender);

// TODO: We should remove the :empty CSS selectors from site-designer.less that affect embeds and just let it be decorated like other atoms
const config = Atom.create({
  displayName: 'Code Embed',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Embed'), iconName('HtmlEmbed')),
  },
  showEmpty: false,
  pathToReplaceableString: ['embed', 'meta', 'html'],
  render: HtmlEmbed,
  dataType: createIntegratedRootAliasInstance(
    qualify(Plugins.name('Embed'), typeName('Code'))
  ),
});

export default config;
